import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import useSWR, { mutate } from 'swr';
import api from 'utils/api';
import { DEFAULT_OFFER_IMAGE, LEGACY_OFFER_IMAGE, hasOfferImage } from 'components/offers/offerImages';

const defaultImageKey = (businessId) => `/offer/${businessId}/images/default`;

const fetchDefaultImage = async (key) => {
  const response = await api.get(key);
  if (!hasOfferImage(response?.url)) throw new Error('Default image is unavailable.');
  return response.url;
};

export const refreshOfferDefaultImage = (businessId) => {
  if (businessId != null) void mutate(defaultImageKey(businessId)).catch(() => {});
};

export default function useOfferImagePreview(businessId, selectedImage, useBusinessDefault = true) {
  const needsDefault = useBusinessDefault && !hasOfferImage(selectedImage);
  const { data, error, isLoading, mutate: retry } = useSWR(
    needsDefault && businessId != null ? defaultImageKey(businessId) : null,
    fetchDefaultImage,
    { shouldRetryOnError: false, revalidateOnFocus: false },
  );

  return {
    imageUrl: hasOfferImage(selectedImage) ? selectedImage
      : !useBusinessDefault ? LEGACY_OFFER_IMAGE
      : businessId == null ? DEFAULT_OFFER_IMAGE : data || '',
    error: needsDefault ? error : undefined,
    isLoading: needsDefault && isLoading,
    retry: () => { void retry().catch(() => {}); },
  };
}

export function OfferImagePreviewStatus({ error, isLoading, retry }) {
  if (error) return <Box role="alert" sx={{ my: 1 }}>
    Couldn’t load the default image. <Button size="small" onClick={retry}>Retry</Button>
  </Box>;
  if (isLoading) return <Typography role="status" variant="caption">Loading default image…</Typography>;
  return null;
}
